/**
 * Native Patient Summary — the first page to open the Application Designer.
 *
 * The page is still the application. It renders its own React cards, it is handed
 * the data its host already fetched and permission-checked, and it decides what
 * each card id means through `renderSummaryCard`. What it no longer owns is the
 * editor: the Design button, the draft, undo/redo, the properties panel, the
 * hidden tray and the cosmetics all come from `lib/applicationDesigner` and
 * `components/applicationDesigner`, so the next designable page inherits them
 * rather than copying this file.
 *
 * Design happens IN PLACE. Nothing unmounts and nothing is re-fetched when the
 * Design button is pressed — the same cards the clinician was reading are the
 * ones being arranged, which is what keeps data fetching and permissions with
 * the native page instead of moving them into a designer route.
 *
 * `renderSummaryCard` is the only thing that turns a saved id into a component,
 * and it is a closed switch. A layout can therefore only ever name a card this
 * file already compiled in.
 */
import { useCallback, useMemo } from "react";
import { PAT } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import {
  AiPatientSummaryCard,
  AllergiesCard,
  HealthMaintenanceSummaryCard,
  MedicalHistoryCard,
  MedicationsSummaryCard,
  OrdersResultsSummaryCard,
  PatientSnapshotCard,
  ProblemListCard,
  RecentEncountersCard,
  SurgicalHistoryCard,
  VitalSignsSummaryCard,
} from "@/components/patient/PatientSummaryCards";
import ApplicationDesignerBar, {
  HiddenElementsTray,
} from "@/components/applicationDesigner/ApplicationDesignerBar";
import DesignableItem from "@/components/applicationDesigner/DesignableItem";
import DesignerProperties from "@/components/applicationDesigner/DesignerProperties";
import FlowDesignChrome from "@/components/applicationDesigner/FlowDesignChrome";
import { useApplicationDesigner } from "@/lib/applicationDesigner/useApplicationDesigner";
import {
  descriptorElementTitle,
  elementAllowedForAccess,
} from "@/lib/applicationDesigner/pageDescriptor";
import { PATIENT_SUMMARY_COLUMNS, patientSummaryDescriptor } from "@/lib/patientSummaryPage";
import {
  getPatientSummaryLayout,
  savePatientSummaryLayout,
} from "@/lib/patientSummaryLayoutApi";
import "@/styles/emr-application-designer.css";

const DESIGN_TEST_IDS = {
  toggle: PAT.summaryDesignToggle,
  banner: PAT.summaryDesignBanner,
  save: PAT.summaryDesignSave,
  cancel: PAT.summaryDesignCancel,
  reset: PAT.summaryDesignReset,
  undo: PAT.summaryDesignUndo,
  redo: PAT.summaryDesignRedo,
  hidden: PAT.summaryDesignHidden,
  restore: PAT.summaryDesignRestore,
  hide: PAT.summaryDesignHide,
  moveUp: PAT.summaryDesignMoveUp,
  moveDown: PAT.summaryDesignMoveDown,
  moveLeft: PAT.summaryDesignMoveLeft,
  moveRight: PAT.summaryDesignMoveRight,
  column: PAT.summaryDesignColumn,
  item: PAT.summaryCard,
  select: PAT.summaryDesignSelect,
  properties: PAT.summaryDesignProperties,
  propertiesEmpty: PAT.summaryDesignPropertiesEmpty,
  property: PAT.summaryDesignProperty,
  propertyClear: PAT.summaryDesignPropertyClear,
};

const DESIGN_BANNER =
  "Organization-wide Patient Summary layout. Move, hide, restore or restyle existing cards. "
  + "Clinical data and card behavior are unchanged.";

function renderSummaryCard(id, props) {
  switch (id) {
    case "problem-list":
      return <ProblemListCard items={props.problemList} onViewAll={props.onOpenProblemList} />;
    case "medications":
      return <MedicationsSummaryCard />;
    case "allergies":
      return (
        <AllergiesCard
          items={props.allergies?.items}
          noKnownAllergies={props.allergies?.noKnownAllergies}
          onViewAll={props.onOpenAllergies}
        />
      );
    case "recent-encounters":
      return (
        <RecentEncountersCard
          encounters={props.encounters}
          onNewEncounter={props.onNewEncounter}
          onViewAll={props.onOpenEncounters}
          onSelectEncounter={props.onSelectEncounter}
        />
      );
    case "orders-results":
      return <OrdersResultsSummaryCard onViewAll={props.onOpenResults} />;
    case "health-maintenance":
      return <HealthMaintenanceSummaryCard />;
    case "vital-signs":
      return <VitalSignsSummaryCard />;
    case "patient-snapshot":
      return (
        <PatientSnapshotCard
          fields={props.fields}
          encounters={props.encounters}
          problemList={props.problemList}
          showProblems={Boolean(props.onOpenProblemList)}
          onOpenFrontDesk={props.onOpenFrontDesk}
        />
      );
    case "ai-patient-summary":
      return <AiPatientSummaryCard />;
    case "medical-history":
      return (
        <MedicalHistoryCard
          conditions={props.medicalHistory?.conditions}
          negatives={props.medicalHistory?.negatives}
          onViewAll={props.onOpenMedicalHistory}
        />
      );
    case "surgical-history":
      return <SurgicalHistoryCard items={props.surgicalHistory?.items} onViewAll={props.onOpenSurgicalHistory} />;
    default:
      return null;
  }
}

export function ClinicalSummary(props) {
  const { user } = useAuth() || {};

  /**
   * The saved layout speaks `cards`; the Application Designer speaks `items`.
   * The native page owns its own wire format, so the translation lives here and
   * the kernel stays page-agnostic.
   */
  const loadLayout = useCallback(() => getPatientSummaryLayout(), []);
  const saveLayout = useCallback(
    (payload) =>
      savePatientSummaryLayout({
        page: payload.page,
        version: payload.version,
        cards: payload.items,
      }),
    [],
  );

  const session = useApplicationDesigner({
    descriptor: patientSummaryDescriptor,
    loadLayout,
    saveLayout,
    user,
    messages: {
      saved: "Patient Summary layout saved for the organization.",
      saveFailed: "Failed to save Patient Summary layout.",
    },
  });

  /**
   * Which cards this viewer may see, derived from the navigation the host was
   * willing to hand over. This is a presentation gate: the real protection is
   * that the host never fetched data it had no permission to read.
   */
  const access = useMemo(
    () => ({
      problemList: Boolean(props.onOpenProblemList),
      allergies: Boolean(props.onOpenAllergies),
      medicalHistory: Boolean(props.onOpenMedicalHistory),
      surgicalHistory: Boolean(props.onOpenSurgicalHistory),
    }),
    [
      props.onOpenProblemList,
      props.onOpenAllergies,
      props.onOpenMedicalHistory,
      props.onOpenSurgicalHistory,
    ],
  );

  const { designing } = session;

  return (
    <div className={designing ? "ad-designing" : undefined} data-testid="patient-summary">
      <ApplicationDesignerBar
        session={session}
        testIds={DESIGN_TEST_IDS}
        bannerText={DESIGN_BANNER}
        designLabel="Design View"
      />

      <div className="emr-chart-grid">
        {Array.from({ length: PATIENT_SUMMARY_COLUMNS }, (_, column) => {
          const items = session
            .inColumn(column)
            .filter((item) =>
              elementAllowedForAccess(session.descriptor, item.id, access, { designing }),
            );
          return (
            <div
              key={column}
              className="emr-chart-col"
              data-testid={`${PAT.summaryDesignColumn}-${column}`}
              onDragOver={
                designing
                  ? (event) => {
                      event.preventDefault();
                    }
                  : undefined
              }
              onDrop={
                designing
                  ? (event) => {
                      event.preventDefault();
                      const sourceId = event.dataTransfer.getData("text/plain");
                      if (sourceId) session.place(sourceId, column);
                    }
                  : undefined
              }
            >
              {items.map((item) => {
                const body = renderSummaryCard(item.id, props);
                if (!body) return null;
                const card = (
                  <DesignableItem item={item} testId={`${PAT.summaryCard}-${item.id}`}>
                    {body}
                  </DesignableItem>
                );
                if (!designing) return <div key={item.id}>{card}</div>;
                return (
                  <FlowDesignChrome
                    key={item.id}
                    session={session}
                    item={item}
                    column={column}
                    title={descriptorElementTitle(session.descriptor, item.id)}
                    testIds={DESIGN_TEST_IDS}
                  >
                    {card}
                  </FlowDesignChrome>
                );
              })}
            </div>
          );
        })}
      </div>

      <DesignerProperties session={session} testIds={DESIGN_TEST_IDS} />

      <HiddenElementsTray session={session} testIds={DESIGN_TEST_IDS} title="Hidden cards" />
    </div>
  );
}

export default ClinicalSummary;
